import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import Form from '@gef-ui/form-material/Form';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { MaxLengths } from 'constants/forms';
import { EMPTY_VALUES } from 'constants/general';
import fetch from 'features/common/fetch';
import { toUserName } from 'features/other/organizationStructureMUI/utils';
import subsellerSearch from 'features/views/subsellerSearch';
import formDescriptorFn, { FIELDS, SUBSELLER_SEARCH_FORM_NAME } from 'features/views/subsellerSearch/form';
import { mailToLink } from 'utils';

const fetchStorePath = `${fetch.constants.urls.userList}.subsellerSearch`;

const SubordinateLoginSearchModalBody = ({ onSelectSubsellerLogin }) => {
	const dispatch = useDispatch();

	const fetchState = useSelector(fetch.selectors.getFetchState(fetchStorePath));
	const subsellers = useSelector(subsellerSearch.selectors.getSubsellers);
	const hasRecords = !isNilOrEmpty(subsellers || EMPTY_VALUES.ARRAY);

	const handleReload = useCallback(() => {
		dispatch(subsellerSearch.actions.reload());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(subsellerSearch.actions.submitForm());
	}, [dispatch]);

	const handleReset = useCallback(() => {
		dispatch(subsellerSearch.actions.resetCriteria());
	}, [dispatch]);

	const handleLoadMore = useCallback(() => {
		dispatch(subsellerSearch.actions.loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(subsellerSearch.actions.loadPage(page - 1));
		},
		[dispatch],
	);

	useEffect(
		() => () => {
			dispatch(subsellerSearch.actions.resetCriteria());
		},
		[dispatch],
	);

	return (
		<Form name={SUBSELLER_SEARCH_FORM_NAME} formDescriptorFn={formDescriptorFn}>
			<Stack spacing={5}>
				<Grid container spacing={3}>
					<Grid size={12}>
						<Grid container spacing={2}>
							<Grid size={4}>
								<TextField
									size="small"
									fieldName={FIELDS.subsellerLogin}
									label={getLabelMessage('subsellerSearch:subsellerLogin')}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.LOGIN,
										},
									}}
								/>
							</Grid>
							<Grid size={4}>
								<TextField
									size="small"
									fieldName={FIELDS.subsellerSurname}
									label={getLabelMessage('subsellerSearch:subsellerSurname')}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.SURNAME,
										},
									}}
								/>
							</Grid>
							<Grid size={4}>
								<TextField
									size="small"
									fieldName={FIELDS.subsellerFirstName}
									label={getLabelMessage('subsellerSearch:subsellerFirstName')}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.NAME,
										},
									}}
								/>
							</Grid>
							<Grid size={12}>
								<Grid
									container
									spacing={2}
									sx={{
										justifyContent: 'flex-end',
									}}
								>
									<Grid size={3}>
										<Button variant="outlined" onClick={handleReset} fullWidth>
											{getLabelMessage('common:resetButton')}
										</Button>
									</Grid>
									<Grid size={3}>
										<SubmitButton
											variant="contained"
											onClick={handleSubmit}
											fullWidth
											text={getLabelMessage('common:searchButton')}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				<ComponentLoader fetchStorePath={fetchStorePath} onReload={handleReload}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ width: '200px' }}>
									{getLabelMessage('subsellerSearch:subsellerLogin')}
								</TableCell>
								<TableCell sx={{ width: '200px' }}>
									{getLabelMessage('subsellerSearch:subsellerFirstName')}
								</TableCell>
								<TableCell>{getLabelMessage('subsellerSearch:subsellerEmail')}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{!hasRecords ? (
								<TableHasNoResults fetchState={fetchState} />
							) : (
								subsellers.map((subseller) => (
									<TableRow>
										<TableCell sx={{ color: 'primary.main', cursor: 'pointer' }}>
											<span onClick={onSelectSubsellerLogin(subseller.login)}>
												{subseller.login}
											</span>
										</TableCell>
										<TableCell>
											{
												toUserName({
													...subseller,
													existsUsersWithTheSameName: false,
												}).fullName
											}
										</TableCell>
										<TableCell>{mailToLink(subseller.email, true)}</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
						<PaginationRow
							fetchStorePath={fetchStorePath}
							colSpan={100}
							onLoadNext={handleLoadMore}
							onPaginationClick={handlePaginationClick}
						/>
					</Table>
				</ComponentLoader>
			</Stack>
		</Form>
	);
};

SubordinateLoginSearchModalBody.propTypes = {
	onSelectSubsellerLogin: PropTypes.func,
};

export default SubordinateLoginSearchModalBody;
